import { useEffect } from 'react';
import { di } from 'react-magnetic-di';
import { isFieldTypeSupported, useIssueAdjustmentsActions } from '@atlassian/ui-modifications-core/controllers';
import type { InitializeFieldPayload } from '@atlassian/ui-modifications-core/types';
import { REQUEST_CREATE_VIEW_TYPE } from '../../../common/constants';

import { getValueByInitializeFieldPropertyMapping } from '../../../common/utils/configuration/utils';
import type { FieldState } from 'state/persisted/request-create/types';

type FieldStateWithRendererType = FieldState & {
    rendererType: string;
};

const hasRendererType = (field: FieldState): field is FieldStateWithRendererType => {
    // Indeed it is typed as any, replace with Object.hasOwn once available in help-center.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Object.prototype.hasOwnProperty.call(field, 'rendererType');
};

export const useInitializeFields = ({ fields }: { fields: FieldState[] }) => {
    di(useIssueAdjustmentsActions, isFieldTypeSupported);
    const { initializeFields } = useIssueAdjustmentsActions();

    useEffect(() => {
        const fieldsToInitialize: InitializeFieldPayload[] = fields
            // Remove 'as string' on isUiModificationsSupportEnabled, ditto_uim_-_jsm_support_main FF cleanup
            .filter(({ jiraFieldType }) => isFieldTypeSupported(REQUEST_CREATE_VIEW_TYPE, jiraFieldType as string))
            .map((field) => {
                return {
                    field: {
                        fieldName: field.label,
                        fieldId: field.id,
                        description: field.description,
                        descriptionRaw: field.description,
                        value: getValueByInitializeFieldPropertyMapping({
                            field,
                            // Remove 'as string' on isUiModificationsSupportEnabled, ditto_uim_-_jsm_support_main FF cleanup
                            fieldType: field.jiraFieldType as string,
                            property: 'value',
                            defaultValue: null,
                        }),
                        isRequired: field.required ?? false,
                        isVisible: field.displayed,
                        isReadOnly: field.disabled ?? false,
                        // Remove 'as string' on isUiModificationsSupportEnabled, ditto_uim_-_jsm_support_main FF cleanup
                        fieldType: field.jiraFieldType as string,
                        allowedValues: getValueByInitializeFieldPropertyMapping({
                            field,
                            // Remove 'as string' on isUiModificationsSupportEnabled, ditto_uim_-_jsm_support_main FF cleanup
                            fieldType: field.jiraFieldType as string,
                            property: 'allowedValues',
                            defaultValue: undefined,
                        }),
                    },
                    metadata: { fieldRenderer: hasRendererType(field) ? field.rendererType : undefined },
                };
            });

        initializeFields(fieldsToInitialize);

        // No deps, intialize once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
